import { useAuth0 } from "@auth0/auth0-react";
import { Capacitor } from "@capacitor/core";
import { useCallback, useRef } from "react";
import { useSessionTimeout } from "../../hooks/useSessionTimeout";
import { useAnalytics } from "../../lib/analytics";
import { performLogout } from "../../lib/auth-logout";
import { SessionTimeoutWarningModal } from "./SessionTimeoutWarningModal";

export function SessionTimeoutHandler() {
	const { isAuthenticated, logout, getAccessTokenSilently } = useAuth0();
	const analytics = useAnalytics();
	const analyticsRef = useRef(analytics);
	analyticsRef.current = analytics;

	const logoutRef = useRef(logout);
	logoutRef.current = logout;

	const getTokenRef = useRef(getAccessTokenSilently);
	getTokenRef.current = getAccessTokenSilently;

	const handleWarning = useCallback(() => {
		analyticsRef.current.logUserAction("session_timeout_warning_shown", {
			platform: Capacitor.getPlatform(),
		});
	}, []);

	const handleTimeout = useCallback(() => {
		analyticsRef.current.logAuth("logout", {
			trigger: "session_timeout",
			platform: Capacitor.getPlatform(),
		});
		performLogout(logoutRef.current);
	}, []);

	const { isWarning, countdown, resetTimer } = useSessionTimeout({
		onWarning: handleWarning,
		onTimeout: handleTimeout,
		enabled: isAuthenticated,
	});

	const handleStayLoggedIn = useCallback(() => {
		analyticsRef.current.logUserAction("session_timeout_stay_logged_in", {
			platform: Capacitor.getPlatform(),
		});
		resetTimer();

		// Proactively refresh token to prevent stale token after idle period
		getTokenRef
			.current({
				authorizationParams: {
					audience: import.meta.env.VITE_AUTH0_AUDIENCE,
				},
				cacheMode: "off",
			})
			.catch((error) => {
				console.error("Failed to refresh token on stay logged in:", error);
			});
	}, [resetTimer]);

	const handleLogOut = useCallback(() => {
		analyticsRef.current.logAuth("logout", {
			trigger: "session_timeout_manual",
			platform: Capacitor.getPlatform(),
		});
		analyticsRef.current.logUserAction("session_timeout_manual_logout", {
			platform: Capacitor.getPlatform(),
		});
		performLogout(logoutRef.current);
	}, []);

	if (!isWarning) return null;

	return (
		<SessionTimeoutWarningModal
			countdown={countdown}
			onStayLoggedIn={handleStayLoggedIn}
			onLogOut={handleLogOut}
		/>
	);
}
